import * as React from 'react';
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Chip, Divider } from '@mui/material';

const ROJO = '#c0000a';

const cop = (n) => Number(n || 0).toLocaleString('es-CO', { maximumFractionDigits: 0 });
const money = (n, mon) =>
  `${Number(n || 0).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${mon || ''}`.trim();

const TITULOS = {
  REPUESTOS_MP: 'Mantenimiento preventivo',
  INSTALACION: 'Instalación',
  GARANTIA_EXTENDIDA: 'Garantía extendida',
};

const headSx = { '& th': { backgroundColor: '#f6f6f6', fontWeight: 'bold', fontSize: '0.9rem' } };
const rowSx = { '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } };
const cellSx = { fontSize: '0.9rem' };

// ¿La nota trae contenido para mostrar? (items sueltos o grupos)
function tieneContenido(s) {
  if (!s) return false;
  if (Array.isArray(s.items) && s.items.length) return true;
  if (Array.isArray(s.grupos) && s.grupos.length) return true;
  return false;
}

// Contenedor comun: encabezado rojo, contenido y total.
function Shell({ titulo, subtitulo, chip, children, totalCOP }) {
  return (
    <Paper elevation={2} sx={{ overflow: 'hidden', borderRadius: '8px', border: `1px solid ${ROJO}22` }}>
      <Box sx={{ backgroundColor: ROJO, color: '#fff', px: 2, py: 1.5 }}>
        <Typography sx={{ fontWeight: 'bold', fontSize: '1.1rem', letterSpacing: '.3px' }}>{titulo}</Typography>
        {subtitulo ? <Typography sx={{ fontSize: '0.9rem', opacity: 0.95 }}>{subtitulo}</Typography> : null}
      </Box>
      {chip ? (
        <Box sx={{ px: 2, pt: 1.5 }}>
          <Chip label={chip} size="small" sx={{ backgroundColor: `${ROJO}14`, color: ROJO, fontWeight: 'bold' }} />
        </Box>
      ) : null}
      {children}
      {totalCOP != null ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', gap: 1, backgroundColor: '#fbe9e9', px: 2, py: 1.5 }}>
          <Typography sx={{ color: ROJO, fontWeight: 'bold', fontSize: '0.95rem' }}>Total:</Typography>
          <Typography sx={{ color: ROJO, fontWeight: 'bold', fontSize: '1.15rem' }}>{cop(totalCOP)} COP</Typography>
        </Box>
      ) : null}
    </Paper>
  );
}

// Tabla de repuestos de un equipo.
function TablaRepuestos({ items }) {
  return (
    <TableContainer sx={{ mt: 1 }}>
      <Table size="small">
        <TableHead><TableRow sx={headSx}>
          <TableCell>SKU</TableCell><TableCell>Descripción</TableCell>
          <TableCell align="right">Cant.</TableCell><TableCell align="right">Precio unit.</TableCell>
          <TableCell align="right">Subtotal (COP)</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {items.map((it, i) => (
            <TableRow key={it.sku || i} sx={rowSx}>
              <TableCell sx={cellSx}>{it.sku}</TableCell>
              <TableCell sx={cellSx}>{it.descripcion}</TableCell>
              <TableCell align="right" sx={cellSx}>{it.cantidad}</TableCell>
              <TableCell align="right" sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                {money(it.precio, it.moneda)}
                {it.precioCOP && it.moneda !== 'COP' ? <Typography component="span" sx={{ display: 'block', fontSize: '0.75rem', color: '#777' }}>≈ {cop(it.precioCOP)} COP</Typography> : null}
              </TableCell>
              <TableCell align="right" sx={{ ...cellSx, fontWeight: 600, whiteSpace: 'nowrap' }}>{cop(it.subtotalCOP)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// Bloque de un equipo (nombre, frecuencia, tabla y subtotal).
function GrupoRepuestos({ g, mostrarSubtotal }) {
  const eq = g.equipo || {};
  return (
    <Box sx={{ px: 2, pt: 2 }}>
      <Typography sx={{ fontWeight: 'bold', fontSize: '0.98rem' }}>
        {eq.nombre || ''}{eq.sku ? ` · SKU ${eq.sku}` : ''}
      </Typography>
      {g.frecuencia ? (
        <Chip label={`Frecuencia: ${g.frecuencia}`} size="small" sx={{ mt: 0.5, backgroundColor: `${ROJO}14`, color: ROJO, fontWeight: 'bold' }} />
      ) : null}
      <TablaRepuestos items={g.items || []} />
      {mostrarSubtotal && g.totalCOP != null ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', gap: 1, pt: 1 }}>
          <Typography sx={{ fontSize: '0.85rem', color: '#555' }}>Subtotal equipo:</Typography>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>{cop(g.totalCOP)} COP</Typography>
        </Box>
      ) : null}
    </Box>
  );
}

function MantenimientoPreventivo({ s }) {
  // Normaliza a lista de grupos (soporta un solo equipo o varios en `grupos`).
  const grupos = Array.isArray(s.grupos) && s.grupos.length
    ? s.grupos
    : [{ equipo: s.equipo, frecuencia: s.frecuencia, items: s.items || [], totalCOP: s.totalCOP }];
  const varios = grupos.length > 1;

  return (
    <Paper elevation={2} sx={{ overflow: 'hidden', borderRadius: '8px', border: `1px solid ${ROJO}22` }}>
      <Box sx={{ backgroundColor: ROJO, color: '#fff', px: 2, py: 1.5 }}>
        <Typography sx={{ fontWeight: 'bold', fontSize: '1.1rem', letterSpacing: '.3px' }}>{TITULOS.REPUESTOS_MP}</Typography>
        {varios ? <Typography sx={{ fontSize: '0.9rem', opacity: 0.95 }}>{grupos.length} equipos</Typography> : null}
      </Box>

      {grupos.map((g, i) => (
        <React.Fragment key={i}>
          {i > 0 ? <Divider sx={{ mt: 1 }} /> : null}
          <GrupoRepuestos g={g} mostrarSubtotal={varios} />
        </React.Fragment>
      ))}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', gap: 1, backgroundColor: '#fbe9e9', px: 2, py: 1.5, mt: 1 }}>
        <Typography sx={{ color: ROJO, fontWeight: 'bold', fontSize: '0.95rem' }}>Total mantenimiento:</Typography>
        <Typography sx={{ color: ROJO, fontWeight: 'bold', fontSize: '1.15rem' }}>{cop(s.totalCOP)} COP</Typography>
      </Box>
    </Paper>
  );
}

function Instalacion({ s }) {
  return (
    <Shell titulo={TITULOS.INSTALACION} subtitulo="Servicio de instalación incluido" totalCOP={s.totalCOP}>
      <TableContainer sx={{ mt: 1 }}>
        <Table size="small">
          <TableHead><TableRow sx={headSx}>
            <TableCell>Equipo</TableCell><TableCell>Sucursal</TableCell>
            <TableCell align="right">Total (COP)</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {s.items.map((it, i) => {
              const eq = it.equipo || {}; const su = it.sucursal || {};
              return (
                <TableRow key={i} sx={rowSx}>
                  <TableCell sx={cellSx}>{eq.descripcion || ''}{eq.sku ? <Typography component="span" sx={{ display: 'block', fontSize: '0.75rem', color: '#777' }}>SKU {eq.sku}</Typography> : null}</TableCell>
                  <TableCell sx={cellSx}>{su.nombre || ''}{su.id ? ` (${su.id})` : ''}</TableCell>
                  <TableCell align="right" sx={{ ...cellSx, fontWeight: 600, whiteSpace: 'nowrap' }}>{cop(it.totalCOP)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Shell>
  );
}

function GarantiaExtendida({ s }) {
  return (
    <Shell titulo={TITULOS.GARANTIA_EXTENDIDA} subtitulo="Cobertura adicional para tu equipo" totalCOP={s.totalCOP}>
      <TableContainer sx={{ mt: 1 }}>
        <Table size="small">
          <TableHead><TableRow sx={headSx}>
            <TableCell>Equipo</TableCell><TableCell align="right">Cant.</TableCell>
            <TableCell align="right">Base (COP)</TableCell><TableCell align="right">Valor (COP)</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {s.items.map((it, i) => {
              const eq = it.equipo || {};
              return (
                <TableRow key={i} sx={rowSx}>
                  <TableCell sx={cellSx}>{eq.descripcion || ''}{eq.sku ? <Typography component="span" sx={{ display: 'block', fontSize: '0.75rem', color: '#777' }}>SKU {eq.sku}</Typography> : null}</TableCell>
                  <TableCell align="right" sx={cellSx}>{it.cantidadEquipo}</TableCell>
                  <TableCell align="right" sx={cellSx}>{cop(it.baseCOP)}</TableCell>
                  <TableCell align="right" sx={{ ...cellSx, fontWeight: 600, whiteSpace: 'nowrap' }}>{cop(it.valorCOP)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Shell>
  );
}

export default function ServicioDetalle({ servicio }) {
  if (!tieneContenido(servicio)) return null;
  if (servicio.tipo === 'REPUESTOS_MP') return <MantenimientoPreventivo s={servicio} />;
  if (servicio.tipo === 'INSTALACION') return <Instalacion s={servicio} />;
  if (servicio.tipo === 'GARANTIA_EXTENDIDA') return <GarantiaExtendida s={servicio} />;
  return null;
}

export { TITULOS, tieneContenido };
